import * as stylex from "@stylexjs/stylex";
import { ChevronRight, CloudOff, CloudSun, type LucideIcon, Umbrella } from "lucide-react";
import { copy } from "@/copy";
import type { AccountSettings, Weather } from "@/domain/schemas";
import { displayTemp } from "@/domain/temperature";
import { type Clock, clockAt, formatHour, minutesOfDay } from "@/domain/time";
import { rainHour } from "@/domain/weather";
import type { ZoneState } from "@/domain/zoneState";
import { conditionText } from "@/features/weather";
import { radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Icon } from "@/ui/Icon";
import { press } from "@/ui/press";
import { Skeleton, SkeletonBlock } from "@/ui/Skeleton";
import { weatherIcons } from "@/ui/weatherIcons";
import { Zone } from "@/ui/Zone";
import { CueEyebrow } from "./CueEyebrow";
type NowWeatherProps = {
  state: ZoneState<Weather>;
  retryIn: number;
  unit: AccountSettings["tempUnit"];
  clock: Clock;
  tz: string;
  onOpenWeather: () => void;
  onSetLocation: () => void;
  small?: boolean;
};
type ReadingProps = {
  weather: Weather;
  updatedAt: string | undefined;
  unit: AccountSettings["tempUnit"];
  clock: Clock;
  tz: string;
  onOpen: () => void;
};
type MessageProps = {
  icon: LucideIcon;
  title: string;
  body: string;
};
const styles = {
  button: {
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kmVPX3: "x1717udv",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "x1yc453h",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  reading: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1v2ro7d",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  row: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1v2ro7d",
    k7Eaqz: "xeuugli",
    kAzted: "x879a55",
    $$css: true
  },
  message: {
    kAzted: "xbktkl8",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xou54vl",
    $$css: true
  },
  skeleton: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1v2ro7d",
    $$css: true
  }
};
export function NowWeather({
  state,
  retryIn,
  unit,
  clock,
  tz,
  onOpenWeather,
  onSetLocation,
  small = false
}: NowWeatherProps) {
  const Shown = small ? Row : Reading;
  return <Zone state={state} loading={<NowWeatherSkeleton />} empty={<button type="button" onClick={onSetLocation} {...stylex.props(styles.button, styles.message, press.scale)}>
          <Message icon={CloudSun} title={copy.today.weather.setTitle} body={copy.today.weather.setBody} />
        </button>} failed={<div className="xbktkl8 x78zum5 x6s0dn4 xou54vl">
          <Message icon={CloudOff} title={copy.today.unavailable.weather} body={copy.today.weather.failedBody(retryIn)} />
        </div>}>
      {([weather], updatedAt) => weather && <Shown weather={weather} updatedAt={updatedAt} unit={unit} clock={clock} tz={tz} onOpen={onOpenWeather} />}
    </Zone>;
}
function Reading({
  weather,
  updatedAt,
  unit,
  clock,
  tz,
  onOpen
}: ReadingProps) {
  const temp = (fahrenheit: number) => displayTemp(fahrenheit, unit);
  const rain = rainHour(weather.hourly);
  return <button type="button" aria-label={copy.today.weather.name} onClick={onOpen} {...stylex.props(styles.button, styles.reading, press.scale)}>
      <CueEyebrow>{placeLabel(weather, updatedAt, clock, tz)}</CueEyebrow>
      <span className="x78zum5 x6s0dn4 xou54vl">
        <Icon icon={weatherIcons[weather.current.icon]} size={64} strokeWidth={1.25} />
        <span className="x8vhlsp x1iikomf xo5v014 xo2cfqc xss6m8b">{`${temp(weather.current.temp)}°`}</span>
      </span>
      <span className="x78zum5 xdt5ytf x17d4w8g">
        <span className="x12feioo x15i68ds xwn7fz2">
          {copy.today.weather.summary(conditionText(weather.current.condition), temp(weather.current.high), temp(weather.current.low))}
        </span>
        {rain && <span className="x78zum5 x6s0dn4 x167g77z x12feioo x1cf7cre xwn7fz2">
            <Icon icon={Umbrella} size={20} />
            {copy.today.weather.rainFrom(formatHour(minutesOfDay(new Date(rain), tz), clock))}
          </span>}
      </span>
    </button>;
}
function Row({
  weather,
  unit,
  clock,
  tz,
  onOpen
}: ReadingProps) {
  const temp = (fahrenheit: number) => displayTemp(fahrenheit, unit);
  const rain = rainHour(weather.hourly);
  return <button type="button" aria-label={copy.today.weather.name} onClick={onOpen} {...stylex.props(styles.button, styles.row, press.scale)}>
      <Icon icon={weatherIcons[weather.current.icon]} size={40} strokeWidth={1.25} />
      <span className="x102p7hv x1iikomf xo5v014 xo2cfqc xss6m8b">{`${temp(weather.current.temp)}°`}</span>
      <span className="x78zum5 xdt5ytf x195vfkc x1iyjqo2 xeuugli">
        <span className="x78zum5 x6s0dn4 x17d4w8g x1jvydc1 x15i68ds xwn7fz2">
          {copy.today.weather.highLow(temp(weather.current.high), temp(weather.current.low))}
        </span>
        {rain && <span className="x78zum5 x6s0dn4 x17d4w8g x1jvydc1 xwn7fz2 x1cf7cre">
            <Icon icon={Umbrella} size={16} />
            {copy.today.weather.rainFrom(formatHour(minutesOfDay(new Date(rain), tz), clock))}
          </span>}
      </span>
      <Icon icon={ChevronRight} size={18} strokeWidth={2} />
    </button>;
}
function Message({
  icon,
  title,
  body
}: MessageProps) {
  return <>
      <Icon icon={icon} size={40} strokeWidth={1.25} />
      <span className="x78zum5 xdt5ytf x1jnr06f">
        <span className="x1iig92y xfbjfs5 x132q4wb">{title}</span>
        <span className="x1n63bex x15i68ds">{body}</span>
      </span>
    </>;
}
function NowWeatherSkeleton() {
  return <Skeleton label={copy.today.weather.loading} xstyle={styles.skeleton}>
      <SkeletonBlock width={120} height={14} />
      <SkeletonBlock width={200} height={72} radius={radii.md} />
      <SkeletonBlock width="70%" height={18} />
    </Skeleton>;
}
function placeLabel(weather: Weather, updatedAt: string | undefined, clock: Clock, tz: string): string {
  if (updatedAt === undefined) return copy.today.weather.now(weather.location);
  return copy.zone.updated(weather.location, clockAt(updatedAt, clock, tz));
}